/**
 * Sandbox Agent Hook
 *
 * Hook for interacting with the sandbox agent chat interface.
 * Uses the AI SDK React hook for streaming chat messages.
 *
 * Message history is loaded from the server on mount. When authoring starts,
 * the server saves the initial user message and enqueues a job to get the
 * agent response. This hook uses SSE to stream text deltas from the server-side
 * job, with polling as a fallback if SSE fails.
 *
 * @module hooks/useSandboxAgent
 * @see docs/api/services/sandbox-agent.md
 */

import React from 'react';
import { useChat, type UIMessage } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';
import {
  addSandboxAgentBreadcrumb,
  captureSandboxAgentError,
  type SandboxAgentTelemetryInput,
} from './sandboxAgentTelemetry';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type SandboxAgentMessage = UIMessage;

type UseSandboxAgentOptions = {
  /** Instance ID to connect to */
  instanceId: string | null;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
};

type UseSandboxAgentReturn = {
  /** Chat messages */
  messages: SandboxAgentMessage[];
  /** Current input value */
  input: string;
  /** Whether the agent is generating a response */
  isLoading: boolean;
  /** Whether message history is being loaded */
  isLoadingHistory: boolean;
  /** Chat status */
  status: 'ready' | 'submitted' | 'streaming' | 'error';
  /** Submit the current message */
  sendMessage: () => Promise<void>;
  /** Stop the current generation */
  stop: () => void;
  /** Clear all messages */
  clearMessages: () => void;
  /** Set input value directly */
  setInput: (value: string) => void;
  /** Force re-fetch message history (e.g. after a task trigger creates a new conversation) */
  reload: () => void;
};

// -----------------------------------------------------------------------------
// Transport factory
// -----------------------------------------------------------------------------

function createSandboxAgentTransport(instanceId: string) {
  return new DefaultChatTransport<SandboxAgentMessage>({
    api: `${API_URL}/org/environments/instances/${instanceId}/agent/messages`,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

// -----------------------------------------------------------------------------
// API helpers
// -----------------------------------------------------------------------------

async function fetchMessageHistory(instanceId: string): Promise<SandboxAgentMessage[]> {
  const response = await fetch(
    `${API_URL}/org/environments/instances/${instanceId}/agent/messages`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch message history');
  }

  const json = await response.json();
  if (!json.ok) {
    throw new Error(json.error ?? 'Failed to fetch message history');
  }

  // The server attributes each user turn with an `author` label ("You" for the
  // viewer's own messages, else the teammate's name) — this is one shared
  // per-instance thread. Carry it in UIMessage.metadata so the renderer can
  // show who sent each turn. New in-session messages have no author metadata
  // and correctly fall back to "You" (they're the viewer's own).
  const data = (json.data ?? []) as Array<SandboxAgentMessage & { author?: string }>;
  return data.map(({ author, ...message }) =>
    author ? { ...message, metadata: { author } } : message
  );
}

async function saveAssistantMessage(
  instanceId: string,
  content: string,
  clientMessageId?: string,
  parts?: SandboxAgentMessage['parts']
): Promise<void> {
  const response = await fetch(
    `${API_URL}/org/environments/instances/${instanceId}/agent/messages/save`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role: 'assistant', content, clientMessageId, parts }),
    }
  );

  if (!response.ok) {
    throw await toApiError(response, 'Failed to save assistant message');
  }
}

type SandboxAgentMessageLike = {
  role?: string;
  content?: string | null;
  parts?: SandboxAgentMessage['parts'];
};

export function getSandboxAgentTextContent(message: SandboxAgentMessageLike): string {
  const partsText = message.parts
    ?.filter(
      (part): part is { type: 'text'; text: string } =>
        part.type === 'text' && 'text' in part && typeof part.text === 'string'
    )
    .map((part) => part.text)
    .join('\n');

  return partsText || message.content || '';
}

export function hasServerPersistedAssistantReply(
  history: SandboxAgentMessageLike[],
  content: string
): boolean {
  const lastMessage = history[history.length - 1];
  return lastMessage?.role === 'assistant' && getSandboxAgentTextContent(lastMessage) === content;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/** Max poll attempts before giving up */
const MAX_POLL_ATTEMPTS = 40;

/** Initial polling delay (ms) */
const POLL_INITIAL_DELAY_MS = 2000;

/** Maximum polling delay (ms) */
const POLL_MAX_DELAY_MS = 15_000;

/**
 * Compute exponential backoff delay for polling.
 * Starts at 2s, doubles each attempt, caps at 15s.
 * 40 attempts ≈ 10 minutes total, covering the 5-minute job timeout.
 */
function computePollDelay(attempt: number): number {
  return Math.min(POLL_INITIAL_DELAY_MS * Math.pow(2, attempt), POLL_MAX_DELAY_MS);
}

/** How often the stall watchdog checks for a wedged "responding" state (ms). */
const STALL_CHECK_INTERVAL_MS = 5_000;

/** What the stall watchdog should do after reconciling against server history. */
type StallAction = 'adopt' | 'release' | 'wait';

/**
 * Decide how to recover a stalled "responding" state from the persisted
 * conversation (server truth) vs. the live client messages.
 *
 * - `adopt`: server holds a completed reply that has caught up to the client →
 *   replace messages with history and release the composer.
 * - `release`: no completed reply, but we're past the hard ceiling → the stream
 *   is dead; release the composer without dropping live messages.
 * - `wait`: server is still working (or hasn't caught up to an optimistic
 *   client message) → keep waiting.
 *
 * The `serverLen >= clientLen` guard prevents adopting a stale history that is
 * missing an optimistic, not-yet-persisted user message (e.g. a send whose POST
 * never reached the server), which would otherwise erase the user's input.
 */
export function decideStallReconcile(args: {
  serverLastRole: string | undefined;
  serverLen: number;
  clientLen: number;
  force: boolean;
}): StallAction {
  const { serverLastRole, serverLen, clientLen, force } = args;
  const serverHasReply =
    serverLastRole !== undefined && serverLastRole !== 'user' && serverLen >= clientLen;
  if (serverHasReply) return 'adopt';
  if (force) return 'release';
  return 'wait';
}

/**
 * No observable progress for this long while loading → reconcile against the
 * persisted conversation and release the composer if the server already has a
 * completed reply. Non-destructive, so a modest threshold is safe.
 */
const STALL_SOFT_MS = 75_000;

/**
 * No observable progress for this long → force-release the composer regardless.
 * Set above the sidecar's 5-minute inactivity timeout, so by this point the
 * server stream has definitively ended (or the turn is dead) and unsticking is
 * always the right call.
 */
const STALL_HARD_MS = 330_000;

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useSandboxAgent(options: UseSandboxAgentOptions): UseSandboxAgentReturn {
  const { instanceId, onError } = options;
  const [input, setInput] = React.useState('');
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(false);

  // Track which instanceId has been loaded (not just a boolean) so that
  // switching between two non-null instance IDs triggers a reload.
  const historyLoadedForRef = React.useRef<string | null>(null);

  // Store onError in a ref so it doesn't need to be in effect dependency arrays.
  // This prevents inline arrow callbacks from restarting effects on every render.
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  const telemetryStateRef = React.useRef<{
    instanceId: string | null;
    status: UseSandboxAgentReturn['status'];
    messageCount: number;
    lastMessageId?: string;
    lastMessageRole?: string;
    historyLoadedFor: string | null;
  }>({
    instanceId,
    status: 'ready',
    messageCount: 0,
    historyLoadedFor: null,
  });

  const reportError = React.useCallback(
    (
      errorInput: unknown,
      telemetry: Omit<
        SandboxAgentTelemetryInput,
        | 'instanceId'
        | 'status'
        | 'messageCount'
        | 'lastMessageId'
        | 'lastMessageRole'
        | 'historyLoadedFor'
      >,
      options: { capture?: boolean } = {}
    ) => {
      const state = telemetryStateRef.current;
      const error =
        options.capture === false
          ? errorInput instanceof Error
            ? errorInput
            : new Error(String(errorInput))
          : captureSandboxAgentError(errorInput, {
              ...telemetry,
              instanceId: state.instanceId,
              status: state.status,
              messageCount: state.messageCount,
              lastMessageId: state.lastMessageId,
              lastMessageRole: state.lastMessageRole,
              historyLoadedFor: state.historyLoadedFor,
            });

      onErrorRef.current?.(error);
    },
    []
  );

  // Create transport when instanceId is available
  const transport = React.useMemo(
    () => (instanceId ? createSandboxAgentTransport(instanceId) : null),
    [instanceId]
  );

  // Persist assistant message when streaming completes. The direct OpenCode
  // path now writes terminal assistant replies server-side; this client save
  // remains a fallback for older streams or transient backend persistence
  // failures.
  const handleFinish = React.useCallback(
    async (options: { message: SandboxAgentMessage }) => {
      const { message } = options;
      if (!instanceId || message.role !== 'assistant') return;

      const textContent = getSandboxAgentTextContent(message);

      if (!textContent) return;

      try {
        const history = await fetchMessageHistory(instanceId);
        if (hasServerPersistedAssistantReply(history, textContent)) {
          return;
        }
      } catch (error) {
        console.warn('[SandboxAgent] Failed to verify persisted assistant message:', error);
      }

      try {
        await saveAssistantMessage(instanceId, textContent, message.id, message.parts);
      } catch (error) {
        // Non-blocking: log but don't disrupt the user experience
        console.error('[SandboxAgent] Failed to persist assistant message:', error);
      }
    },
    [instanceId]
  );

  const chat = useChat<SandboxAgentMessage>({
    id: `sandbox-agent-${instanceId ?? 'none'}`,
    transport: transport ?? undefined,
    experimental_throttle: 40,
    onFinish: handleFinish,
    onError: (error) => {
      console.error('[SandboxAgent] Error:', error);
      reportError(error, {
        phase: 'sdk_transport_error',
        transport: 'sdk',
      });
    },
  });

  const { messages, status, sendMessage: sdkSendMessage, stop, setMessages } = chat;

  // Store setMessages in a ref so it doesn't need to be in effect dependency arrays.
  // If useChat returns a new setMessages reference on re-render, this prevents
  // effect teardown/restart which would reset polling backoff counters.
  const setMessagesRef = React.useRef(setMessages);
  setMessagesRef.current = setMessages;

  // Load message history when instanceId becomes available
  React.useEffect(() => {
    if (!instanceId) {
      historyLoadedForRef.current = null;
      return;
    }

    // Don't reload if already loaded for this specific instance
    if (historyLoadedForRef.current === instanceId) {
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const history = await fetchMessageHistory(instanceId);
        if (!cancelled && history.length > 0) {
          setMessagesRef.current(history);
        }
        historyLoadedForRef.current = instanceId;
      } catch (error) {
        console.error('[SandboxAgent] Failed to load history:', error);
        reportError(
          new Error('Could not load chat history. Your previous messages may not be visible.'),
          {
            phase: 'history_load',
            transport: 'http',
            originalErrorMessage: error instanceof Error ? error.message : String(error),
          }
        );
        historyLoadedForRef.current = instanceId;
      } finally {
        if (!cancelled) {
          setIsLoadingHistory(false);
        }
      }
    };

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [instanceId, reportError]);

  // Derive stable primitives for the effect dependency array.
  // Using the `messages` array directly would cause interval churn on every render
  // since useChat returns a new array reference each time.
  const lastMessage = messages[messages.length - 1];
  const lastMessageRole = lastMessage?.role;
  const lastMessageId = lastMessage?.id;

  telemetryStateRef.current = {
    instanceId,
    status,
    messageCount: messages.length,
    lastMessageId,
    lastMessageRole,
    historyLoadedFor: historyLoadedForRef.current,
  };

  // Track streaming assistant message for SSE updates
  const [streamingContent, setStreamingContent] = React.useState<string | null>(null);

  // Stream server-side agent response via SSE when the last message is from the user.
  // Falls back to polling if SSE connection fails.
  React.useEffect(() => {
    if (!instanceId || historyLoadedForRef.current !== instanceId) return;
    if (lastMessageRole !== 'user') return;
    if (status === 'streaming' || status === 'submitted') return;

    let active = true;
    let eventSource: EventSource | null = null;
    let attempt = 0;
    let timeoutId: ReturnType<typeof setTimeout>;
    let usingPolling = false;
    let accumulatedContent = '';

    // Fetch final history from DB when stream completes
    const fetchFinalHistory = async () => {
      if (!active) return;
      try {
        const history = await fetchMessageHistory(instanceId);
        if (active) {
          setStreamingContent(null);
          setMessagesRef.current(history);
        }
      } catch (error) {
        console.error('[SandboxAgent] Failed to fetch final history:', error);
      }
    };

    // --- SSE Streaming ---
    const connectSSE = () => {
      if (!active) return;

      const url = `${API_URL}/org/environments/instances/${instanceId}/agent/stream`;
      eventSource = new EventSource(url, { withCredentials: true });

      eventSource.addEventListener('conversation.text-delta', (event) => {
        if (!active) return;
        try {
          const data = JSON.parse(event.data) as { delta: string };
          accumulatedContent += data.delta;
          setStreamingContent(accumulatedContent);
        } catch (err) {
          console.error('[SandboxAgent] Failed to parse text-delta:', err);
          reportError(err, {
            phase: 'stream_parse_error',
            transport: 'sse',
          });
        }
      });

      eventSource.addEventListener('conversation.complete', () => {
        if (!active) return;
        eventSource?.close();
        void fetchFinalHistory();
      });

      eventSource.addEventListener('conversation.error', (event) => {
        if (!active) return;
        eventSource?.close();
        try {
          const data = JSON.parse(event.data) as { error: string };
          reportError(new Error(data.error), {
            phase: 'stream_terminal_error',
            transport: 'sse',
          });
        } catch {
          reportError(new Error('Agent conversation failed'), {
            phase: 'stream_terminal_error',
            transport: 'sse',
            originalErrorMessage: event.data,
          });
        }
        void fetchFinalHistory();
      });

      eventSource.onerror = () => {
        if (!active) return;
        eventSource?.close();
        eventSource = null;

        // SSE failed — fall back to polling
        if (!usingPolling) {
          console.warn('[SandboxAgent] SSE connection failed, falling back to polling');
          addSandboxAgentBreadcrumb(
            'SSE connection failed, falling back to polling',
            {
              instanceId,
              lastMessageId,
              lastMessageRole,
              messageCount: messages.length,
            },
            'warning'
          );
          usingPolling = true;
          setStreamingContent(null);
          schedulePoll();
        }
      };
    };

    // --- Polling Fallback ---
    const pollStartedAt = Date.now();
    let lastHistoryMessageId: string | undefined;
    let lastHistoryMessageRole: string | undefined;
    let lastHistoryCount: number | undefined;

    const schedulePoll = () => {
      if (!active) return;

      if (attempt >= MAX_POLL_ATTEMPTS) {
        reportError(new Error('The agent is taking too long to respond. Please try again.'), {
          phase: 'poll_timeout',
          transport: 'polling',
          pollAttempt: attempt,
          maxPollAttempts: MAX_POLL_ATTEMPTS,
          elapsedMs: Date.now() - pollStartedAt,
          usingPolling,
          lastHistoryMessageId,
          lastHistoryMessageRole,
          lastHistoryCount,
        });
        return;
      }

      const delay = computePollDelay(attempt);
      timeoutId = setTimeout(pollForResponse, delay);
    };

    const pollForResponse = async () => {
      attempt++;

      try {
        const history = await fetchMessageHistory(instanceId);
        if (!active) return;

        const lastHistoryMsg = history[history.length - 1];
        lastHistoryMessageId = lastHistoryMsg?.id;
        lastHistoryMessageRole = lastHistoryMsg?.role;
        lastHistoryCount = history.length;
        if (lastHistoryMsg && lastHistoryMsg.role !== 'user') {
          // Stop polling — either assistant response received or system error message saved by job
          setMessagesRef.current(history);
          return;
        }
      } catch (error) {
        // Stop polling immediately on auth failures (token expired, forbidden)
        const message = error instanceof Error ? error.message : String(error);
        if (message.includes('401') || message.includes('403')) {
          reportError(
            new Error('Session expired. Please refresh the page.'),
            {
              phase: 'poll_auth',
              transport: 'polling',
              pollAttempt: attempt,
              originalErrorMessage: message,
            },
            { capture: false }
          );
          return; // Don't schedule another poll
        }
        console.error('[SandboxAgent] Failed to poll for response:', error);
        addSandboxAgentBreadcrumb(
          'Polling for sandbox agent response failed',
          {
            instanceId,
            attempt,
            errorMessage: message,
          },
          'warning'
        );
      }

      schedulePoll();
    };

    // Start with SSE
    connectSSE();

    return () => {
      active = false;
      eventSource?.close();
      clearTimeout(timeoutId);
      setStreamingContent(null);
    };
  }, [instanceId, lastMessageRole, lastMessageId, status, messages.length, reportError]);

  const sendMessage = React.useCallback(async () => {
    if (!instanceId || !transport) {
      console.warn('[SandboxAgent] Cannot submit without instanceId');
      return;
    }

    const value = input.trim();
    if (!value) return;

    try {
      setInput('');
      // AI SDK useChat.sendMessage handles optimistic user message insertion internally
      await sdkSendMessage({ text: value });
    } catch (error) {
      setInput(value);
      console.error('[SandboxAgent] Failed to send message', error);
      reportError(error instanceof Error ? error : new Error('Failed to send message'), {
        phase: 'send_message',
        transport: 'sdk',
      });
    }
  }, [instanceId, transport, input, sdkSendMessage, reportError]);

  const stopGeneration = React.useCallback(() => {
    // Full reset: stop() resets the useChat status, but the SSE-fallback
    // streamingContent must be cleared too or isLoading stays true and the
    // composer stays locked. Clearing both makes Stop a guaranteed escape.
    void stop();
    setStreamingContent(null);
  }, [stop]);

  const clearMessages = React.useCallback(() => {
    setMessages([]);
  }, [setMessages]);

  // Force re-fetch message history (e.g. after a task trigger creates a new conversation)
  const reload = React.useCallback(() => {
    historyLoadedForRef.current = null;
    setMessages([]);
  }, [setMessages]);

  // Compute displayed messages: if we're streaming via SSE, append a synthetic assistant message
  const displayedMessages = React.useMemo(() => {
    if (!streamingContent) return messages;

    // Create a synthetic streaming message (UIMessage uses 'parts', not 'content')
    // Use type assertion since we're creating a minimal valid message structure
    const streamingMessage = {
      id: 'streaming-assistant-message',
      role: 'assistant' as const,
      parts: [{ type: 'text' as const, text: streamingContent }],
    } as SandboxAgentMessage;

    return [...messages, streamingMessage];
  }, [messages, streamingContent]);

  // Consider loading if SDK is streaming/submitted OR if we're SSE streaming
  const isLoading = status === 'streaming' || status === 'submitted' || streamingContent !== null;

  // Clear any lingering SSE stream state when the instance goes away (e.g. the
  // parent stops passing an instanceId once the sandbox is no longer running),
  // so isLoading can't pin the composer in a "responding" state.
  React.useEffect(() => {
    if (!instanceId) setStreamingContent(null);
  }, [instanceId]);

  // ---------------------------------------------------------------------------
  // Stall watchdog
  // ---------------------------------------------------------------------------
  // isLoading can stick forever if a stream never delivers a terminal frame: a
  // missed session.idle race on the sidecar, a dropped/half-open connection, or
  // an instance torn down mid-stream. The server persists the assistant reply on
  // completion, so recover by reconciling against that truth rather than waiting
  // for a terminal frame that may never arrive.
  const lastProgressAtRef = React.useRef(Date.now());
  const progressKeyRef = React.useRef('');
  const reconcilingRef = React.useRef(false);
  const messagesLenRef = React.useRef(messages.length);
  messagesLenRef.current = messages.length;

  // Keep stop() in a ref so reconcileStall stays referentially stable
  // regardless of whether useChat returns a fresh stop each render — otherwise
  // the watchdog interval would tear down and re-arm on every render.
  const stopRef = React.useRef(stop);
  stopRef.current = stop;

  // Fingerprint of observable progress: status, message count/id, the last
  // message's part count (so tool-call activity counts as progress, not just
  // text), its streamed text length, and SSE streamingContent length. Memoized
  // so the reduce doesn't run on every throttled streaming render.
  const lastMsgPartsLen = lastMessage?.parts?.length ?? 0;
  const lastMsgTextLen = React.useMemo(
    () =>
      lastMessage?.parts?.reduce(
        (sum, part) =>
          sum +
          (part.type === 'text' && 'text' in part && typeof part.text === 'string'
            ? part.text.length
            : 0),
        0
      ) ?? 0,
    [lastMessage]
  );
  const progressKey = `${status}|${messages.length}|${lastMessageId ?? ''}|${lastMsgPartsLen}|${lastMsgTextLen}|${
    streamingContent?.length ?? -1
  }`;

  React.useEffect(() => {
    if (progressKey !== progressKeyRef.current) {
      progressKeyRef.current = progressKey;
      lastProgressAtRef.current = Date.now();
    }
  }, [progressKey]);

  const reconcileStall = React.useCallback(
    async (force: boolean) => {
      if (!instanceId || reconcilingRef.current) return;
      reconcilingRef.current = true;
      const release = () => {
        setStreamingContent(null);
        void stopRef.current();
      };
      try {
        let history: SandboxAgentMessage[];
        try {
          history = await fetchMessageHistory(instanceId);
        } catch (error) {
          // Couldn't reach the server. Re-arming here would push the hard
          // ceiling out indefinitely while the instance is unreachable, so do
          // NOT reset lastProgressAt — and once past the ceiling, release
          // anyway so the composer never stays wedged on a dead instance.
          addSandboxAgentBreadcrumb(
            'Stall reconcile history fetch failed',
            {
              instanceId,
              force,
              errorMessage: error instanceof Error ? error.message : String(error),
            },
            'warning'
          );
          if (force) release();
          return;
        }

        const last = history[history.length - 1];
        const action = decideStallReconcile({
          serverLastRole: last?.role,
          serverLen: history.length,
          clientLen: messagesLenRef.current,
          force,
        });

        if (action === 'adopt') {
          // Server finished — adopt its truth and release the composer.
          setMessagesRef.current(history);
          release();
        } else if (action === 'release') {
          // Past the sidecar inactivity timeout with no completed turn: the
          // stream is dead. Release the composer without dropping live messages
          // so the user can retry.
          release();
        }
        // 'wait': server still working — re-arm and keep waiting.
        lastProgressAtRef.current = Date.now();
      } finally {
        reconcilingRef.current = false;
      }
    },
    [instanceId]
  );

  React.useEffect(() => {
    if (!instanceId || !isLoading) return;
    const interval = setInterval(() => {
      const stalledMs = Date.now() - lastProgressAtRef.current;
      if (stalledMs < STALL_SOFT_MS) return;
      void reconcileStall(stalledMs >= STALL_HARD_MS);
    }, STALL_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [instanceId, isLoading, reconcileStall]);

  return {
    messages: displayedMessages,
    input,
    isLoading,
    isLoadingHistory,
    status,
    sendMessage,
    stop: stopGeneration,
    clearMessages,
    setInput,
    reload,
  };
}
